import {
  customListPlants,
  customFetchPlant,
  customListObjects,
  customGetObject,
  listCompanies,
  getCompany,
  customPingInventory,
  customListInventory,
  customListAvailableInventory,
  customGetInventoryItem,
} from "../../../graphQL/queries";

import {
  customAddPlant,
  customUpdatePlant,
  customDeletePlant,
  customAddObject,
  customUpdateObject,
  customDeleteObject,
  customAddCompany,
  customUpdateCompany,
  deleteCompany,
  customAddInventoryItem,
  customUpdateInventoryItem,
  customDeleteInventoryItem,
  customDeductInventoryQuantities,
} from "../../../graphQL/mutations";

import {
  createCRUDHandler,
  handleGraphQLResponse,
  client,
} from "./sharedUtils";
import { projectsClient } from "./projectsClient";

// Type definitions for better type safety
interface PlantInput {
  [key: string]: any;
}

interface ObjectInput {
  [key: string]: any;
}

interface CompanyInput {
  [key: string]: any;
}

interface InventoryInput {
  [key: string]: any;
}

export const apiClient = {
  /**
   * Plant Operations
   */

  /** Get all plants */
  getPlants: createCRUDHandler("customListPlants", customListPlants),

  /** Get a specific plant by ID */
  getPlant: createCRUDHandler(
    "customFetchPlant",
    customFetchPlant,
    (plantId: string) => ({ plantId })
  ),

  /** Create a new plant */
  createPlant: createCRUDHandler(
    "customAddPlant",
    customAddPlant,
    (input: PlantInput) => ({ input: JSON.stringify(input) })
  ),

  /** Update an existing plant */
  updatePlant: async (plantId: string, input: PlantInput) => {
    try {
      const response = await client.graphql({
        query: customUpdatePlant,
        variables: { plantId, input: JSON.stringify(input) },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customUpdatePlant;
      if (!responseData) {
        throw new Error("No data returned for operation: customUpdatePlant");
      }

      return handleGraphQLResponse(responseData, "customUpdatePlant");
    } catch (error) {
      console.error("Error in customUpdatePlant:", error);
      throw error;
    }
  },

  /** Delete a plant by ID */
  deletePlant: createCRUDHandler(
    "customDeletePlant",
    customDeletePlant,
    (plantId: string) => ({ plantId })
  ),

  /**
   * Object Operations
   */

  /** Get all objects */
  getObjects: createCRUDHandler("customListObjects", customListObjects),

  /** Get a specific object by ID */
  getObject: createCRUDHandler(
    "customGetObject",
    customGetObject,
    (objectId: string) => ({ objectId })
  ),

  /** Create a new object */
  createObject: createCRUDHandler(
    "customAddObject",
    customAddObject,
    (input: ObjectInput) => ({ input: JSON.stringify(input) })
  ),

  /** Update an existing object */
  updateObject: async (objectId: string, input: ObjectInput) => {
    try {
      const response = await client.graphql({
        query: customUpdateObject,
        variables: { objectId, input: JSON.stringify(input) },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customUpdateObject;
      if (!responseData) {
        throw new Error("No data returned for operation: customUpdateObject");
      }

      return handleGraphQLResponse(responseData, "customUpdateObject");
    } catch (error) {
      console.error("Error in customUpdateObject:", error);
      throw error;
    }
  },

  /** Delete an object by ID */
  deleteObject: createCRUDHandler(
    "customDeleteObject",
    customDeleteObject,
    (objectId: string) => ({ objectId })
  ),

  /**
   * Company Operations
   */

  /** Get all companies */
  getCompanies: async () => {
    try {
      const response = await client.graphql({
        query: listCompanies,
        variables: {},
        authMode: "userPool",
      });

      const responseData = (response as any).data?.listCompanies;
      if (!responseData) {
        throw new Error("No data returned for operation: listCompanies");
      }

      return handleGraphQLResponse(responseData.items, "listCompanies");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error in listCompanies:", error);
      }
      throw error;
    }
  },

  /** Get a specific company by ID */
  getCompany: async (companyId: string) => {
    try {
      const response = await client.graphql({
        query: getCompany,
        variables: { company_id: companyId },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.getCompany;
      if (!responseData) {
        throw new Error("No data returned for operation: getCompany");
      }

      return handleGraphQLResponse(responseData, "getCompany");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error in getCompany:", error);
      }
      throw error;
    }
  },

  /** Create a new company */
  createCompany: createCRUDHandler(
    "customAddCompany",
    customAddCompany,
    (input: CompanyInput) => ({ input: JSON.stringify(input) })
  ),

  /** Update an existing company */
  updateCompany: async (companyId: string, input: CompanyInput) => {
    try {
      const response = await client.graphql({
        query: customUpdateCompany,
        variables: { companyId, input: JSON.stringify(input) },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customUpdateCompany;
      if (!responseData) {
        throw new Error("No data returned for operation: customUpdateCompany");
      }

      return handleGraphQLResponse(responseData, "customUpdateCompany");
    } catch (error) {
      console.error("Error in customUpdateCompany:", error);
      throw error;
    }
  },

  /** Delete a company by ID */
  deleteCompany: async (companyId: string) => {
    try {
      const response = await client.graphql({
        query: deleteCompany,
        variables: { input: { company_id: companyId } },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.deleteCompany;
      if (!responseData) {
        throw new Error("No data returned for operation: deleteCompany");
      }

      return handleGraphQLResponse(responseData, "deleteCompany");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error in deleteCompany:", error);
      }
      throw error;
    }
  },

  /**
   * Inventory Operations
   */

  /** Ping inventory API to test connectivity */
  pingInventory: createCRUDHandler("customPingInventory", customPingInventory),

  /** Get all inventory items for a company */
  listInventory: createCRUDHandler(
    "customListInventory",
    customListInventory,
    (companyId: string) => ({ companyId })
  ),

  /** Get all available inventory items for a company */
  listAvailableInventory: createCRUDHandler(
    "customListAvailableInventory",
    customListAvailableInventory,
    (companyId: string) => ({ companyId })
  ),

  /** Get a specific inventory item by inventory ID */
  getInventoryItem: async (inventoryId: string) => {
    try {
      const response = await client.graphql({
        query: customGetInventoryItem,
        variables: { inventoryId },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customGetInventoryItem;
      if (!responseData) {
        throw new Error(
          "No data returned for operation: customGetInventoryItem"
        );
      }

      return handleGraphQLResponse(responseData, "customGetInventoryItem");
    } catch (error) {
      console.error("Error in customGetInventoryItem:", error);
      throw error;
    }
  },

  /** Create a new inventory item */
  createInventoryItem: createCRUDHandler(
    "customAddInventoryItem",
    customAddInventoryItem,
    (input: InventoryInput) => ({ input: JSON.stringify(input) })
  ),

  /** Update an existing inventory item */
  updateInventoryItem: async (inventoryId: string, input: InventoryInput) => {
    try {
      const response = await client.graphql({
        query: customUpdateInventoryItem,
        variables: { inventoryId, input: JSON.stringify(input) },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customUpdateInventoryItem;
      if (!responseData) {
        throw new Error(
          "No data returned for operation: customUpdateInventoryItem"
        );
      }

      return handleGraphQLResponse(responseData, "customUpdateInventoryItem");
    } catch (error) {
      console.error("Error in customUpdateInventoryItem:", error);
      throw error;
    }
  },

  /** Delete an inventory item by inventory ID */
  deleteInventoryItem: async (inventoryId: string) => {
    try {
      const response = await client.graphql({
        query: customDeleteInventoryItem,
        variables: { inventoryId },
        authMode: "userPool",
      });

      const responseData = (response as any).data?.customDeleteInventoryItem;
      if (!responseData) {
        throw new Error(
          "No data returned for operation: customDeleteInventoryItem"
        );
      }

      return handleGraphQLResponse(responseData, "customDeleteInventoryItem");
    } catch (error) {
      console.error("Error in customDeleteInventoryItem:", error);
      throw error;
    }
  },

  /** Deduct quantities from multiple inventory items */
  deductInventoryQuantities: async (
    company_id: string,
    deductionsData: {
      deductions: Array<{
        inventory_id: string;
        quantity_to_deduct: number;
      }>;
    }
  ) => {
    try {
      const response = await client.graphql({
        query: customDeductInventoryQuantities,
        variables: {
          companyId: company_id,
          input: JSON.stringify(deductionsData),
        },
        authMode: "userPool",
      });

      const responseData = (response as any).data
        ?.customDeductInventoryQuantities;
      if (!responseData) {
        throw new Error(
          "No data returned for operation: customDeductInventoryQuantities"
        );
      }

      return handleGraphQLResponse(
        responseData,
        "customDeductInventoryQuantities"
      );
    } catch (error) {
      console.error("Error in customDeductInventoryQuantities:", error);
      throw error;
    }
  },

  ...projectsClient,
};
